import React, { FC } from 'react';
import {
    Box,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Tooltip,
    Typography,
    CircularProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client/react';
import { getInvoiceBatchesTemplate } from 'apollo/templates/invoice-batches';
import { useNavigate } from 'react-router-dom';
import { formatDate } from 'utils/format';
import { BATCH_STATUS_COLORS, resolveBatchDisplayStatus } from './invoice-batch-status';

type BatchInvoice = {
    _id: string;
    status: string;
    total?: { currency: string; totalIncVat: number };
    deliveryAddress?: { name: string };
};

type FailedInvoice = {
    invoiceId: string;
    clinicName?: string;
    errorMessage: string;
};

type InvoiceBatchRow = {
    _id: string;
    status: string;
    invoiceCount: number;
    customerCount: number;
    totalOrderCount: number;
    createdAt: number;
    invoices: BatchInvoice[];
    failedInvoices: FailedInvoice[];
};

const formatMultiCurrencyTotal = (invoices: BatchInvoice[]): string => {
    const totals: Record<string, number> = {};
    for (const invoice of invoices) {
        if (!invoice.total) continue;
        const { currency, totalIncVat } = invoice.total;
        totals[currency] = (totals[currency] ?? 0) + totalIncVat;
    }
    return (
        Object.entries(totals)
            .map(
                ([currency, amount]) =>
                    `${currency} ${amount.toLocaleString('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            )
            .join(' / ') || '—'
    );
};

const InvoiceBatches: FC = () => {
    const { t: translate } = useTranslation(['invoices']);
    const navigate = useNavigate();

    const { data, loading } = useQuery(getInvoiceBatchesTemplate, {
        variables: { offset: 0, limit: 50 },
        fetchPolicy: 'cache-and-network',
        pollInterval: 1000 * 30,
    });

    const batches: InvoiceBatchRow[] = data?.invoiceBatches?.batches ?? [];

    return (
        <Box>
            {loading && !batches.length ? (
                <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>{translate('invoices:RUN_DATE')}</TableCell>
                                <TableCell align="right">{translate('invoices:CUSTOMERS')}</TableCell>
                                <TableCell align="right">{translate('invoices:NUM_INVOICES')}</TableCell>
                                <TableCell>{translate('invoices:TOTAL_AMOUNT')}</TableCell>
                                <TableCell>{translate('invoices:BATCH_STATUS')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {batches.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5}>
                                        <Typography variant="body2" color="text.secondary" align="center">
                                            {translate('invoices:NO_INVOICE_BATCHES')}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                batches.map((batch) => {
                                    const displayStatus = resolveBatchDisplayStatus(batch);
                                    const totalText = formatMultiCurrencyTotal(batch.invoices);
                                    return (
                                        <TableRow
                                            key={batch._id}
                                            hover
                                            sx={{ cursor: 'pointer' }}
                                            onClick={() => navigate(`/invoices/invoice-batches/${batch._id}`)}
                                        >
                                            <TableCell>{formatDate(new Date(batch.createdAt))}</TableCell>
                                            <TableCell align="right">{batch.customerCount}</TableCell>
                                            <TableCell align="right">{batch.invoiceCount}</TableCell>
                                            <TableCell sx={{ maxWidth: 160 }}>
                                                <Tooltip title={totalText}>
                                                    <Box
                                                        component="span"
                                                        sx={{
                                                            display: 'block',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                        }}
                                                    >
                                                        {totalText}
                                                    </Box>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={translate(`invoices:BATCH_STATUS_${displayStatus}`)}
                                                    color={BATCH_STATUS_COLORS[displayStatus] ?? 'default'}
                                                    size="small"
                                                />
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
};

export default InvoiceBatches;
